import {
  forwardRef,
  type ReactNode,
} from 'react'

import {
  FieldError as AriaFieldError,
  Group,
  Input as AriaInput,
  Label as AriaLabel,
  Text,
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components'

import type {
  FormFieldErrorMessage,
} from './form-field'

import { cn } from '@/shared/lib/cn'

export interface InputGroupProps
  extends Omit<
    AriaTextFieldProps,
    'children' | 'className'
  > {
  label: ReactNode

  startAddon?: ReactNode
  endAddon?: ReactNode

  leadingContent?: ReactNode
  trailingContent?: ReactNode

  description?: ReactNode
  errorMessage?: FormFieldErrorMessage

  className?: string
  placeholder?: string
  groupClassName?: string
  inputClassName?: string
}

export const InputGroup =
  forwardRef<
    HTMLInputElement,
    InputGroupProps
  >(function InputGroup(
    {
      label,
      startAddon,
      endAddon,
      leadingContent,
      trailingContent,
      description,
      errorMessage,
      className,
      placeholder,
      groupClassName,
      inputClassName,
      isRequired = false,
      isInvalid,
      validationBehavior = 'aria',
      ...props
    },
    ref,
  ) {
    const hasStaticError =
      typeof errorMessage === 'string' &&
      errorMessage.trim().length > 0

    const resolvedIsInvalid =
      isInvalid ?? hasStaticError

    return (
      <AriaTextField
        {...props}
        isRequired={isRequired}
        isInvalid={resolvedIsInvalid}
        validationBehavior={validationBehavior}
        className={cn(
          'flex w-full flex-col gap-1.5',
          className,
        )}
      >
        <AriaLabel className="flex items-center gap-1 text-sm font-medium text-(--color-foreground)">
          {label}

          {isRequired ? (
            <span
              aria-hidden="true"
              className="text-(--color-danger)"
            >
              *
            </span>
          ) : null}
        </AriaLabel>

        <Group
          className={cn(
            [
              'flex min-h-10 w-full',
              'items-stretch overflow-hidden',
              'rounded-lg border',
              'border-(--color-border)',
              'bg-(--color-surface)',
              'transition-[border-color,box-shadow]',
              'data-focus-within:border-(--color-primary)',
              'data-focus-within:ring-2',
              'data-focus-within:ring-(--color-primary)/20',
              'data-invalid:border-(--color-danger)',
              'data-invalid:ring-(--color-danger)/20',
              'data-disabled:cursor-not-allowed',
              'data-disabled:opacity-60',
            ],
            groupClassName,
          )}
        >
          {startAddon ? (
            <div className="flex shrink-0 items-center border-r border-(--color-border) bg-(--color-surface-muted) px-3 text-sm text-(--color-muted-foreground)">
              {startAddon}
            </div>
          ) : null}

          <div className="flex min-w-0 flex-1 items-center gap-2 px-3">
            {leadingContent ? (
              <span
                aria-hidden="true"
                className="flex shrink-0 items-center text-(--color-muted-foreground)"
              >
                {leadingContent}
              </span>
            ) : null}

            <AriaInput
              ref={ref}
              className={cn(
                [
                  'min-w-0 flex-1 border-0',
                  'bg-transparent text-sm',
                  'text-(--color-foreground)',
                  'outline-none',
                  'placeholder:text-(--color-muted-foreground)',
                  'disabled:cursor-not-allowed',
                ],
                inputClassName,
              )}
              placeholder={placeholder}
              aria-placeholder={placeholder}
            />

            {trailingContent ? (
              <span className="flex shrink-0 items-center">
                {trailingContent}
              </span>
            ) : null}
          </div>

          {endAddon ? (
            <div className="flex shrink-0 items-center border-l border-(--color-border) bg-(--color-surface-muted) px-3 text-sm text-(--color-muted-foreground)">
              {endAddon}
            </div>
          ) : null}
        </Group>

        {description ? (
          <Text
            slot="description"
            className="text-xs leading-5 text-(--color-muted-foreground)"
          >
            {description}
          </Text>
        ) : null}

        <AriaFieldError className="text-xs leading-5 text-(--color-danger)">
          {errorMessage}
        </AriaFieldError>
      </AriaTextField>
    )
  })
